import { useCascadingVariations } from "../../hooks/useCascadingVariations";
import { MtcCascadingAttributes } from "./MtcCascadingAttributes";
import { MtcProductGallery } from "./MtcProductGallery";

const React = window.EcommerceCore.vendors.React;
const { useMemo, useState, useEffect } = React;
const { useAppSelector } = window.EcommerceCore.common.hooks;

const ProductPriceDisplay =
  window.EcommerceCore.product.components.ProductPriceDisplay;
const QuantityControl = window.EcommerceCore.cart.components.QuantityControl;
const DeliveryEstimateDisplay =
  window.EcommerceCore.product.components.DeliveryEstimateDisplay;
// Retrieve ProductSingleTabs from Core
const ProductSingleTabs =
  window.EcommerceCore.product.components.ProductSingleTabs;

const Skeleton =
  window.EcommerceCore.vendors.ReactLoadingSkeleton.default ||
  window.EcommerceCore.vendors.ReactLoadingSkeleton;

const { useGetProductByIdQuery } = window.EcommerceCore.product.api;
const { useProductAddToCart } = window.EcommerceCore.cart.hooks;

const MtcProductSingle: React.FC = () => {
  // Aktif para birimini Redux üzerinden dinle
  const currentCurrency = useAppSelector(
    (state: any) => state.currency.currentCurrency,
  );

  const productId = useMemo(() => {
    const rootElement = document.getElementById("product-single-root");
    const id = rootElement?.dataset.productId;
    return id ? parseInt(id, 10) : null;
  }, []);

  const {
    data: product,
    isLoading: isProductLoading,
    isError,
    refetch: refetchProduct, // Yeniden yükleme fonksiyonunu dışarı alıyoruz
  } = useGetProductByIdQuery(productId as number, {
    skip: !productId,
  });

  // Para birimi değiştiğinde ana ürünü de yeniden yükle
  useEffect(() => {
    if (productId) {
      refetchProduct();
    }
  }, [currentCurrency, productId, refetchProduct]);

  const {
    selectedAttributes,
    selectedVariation,
    availableTermsPerAttribute,
    handleAttributeSelect,
    handleClear,
    isLoading: isSelectionLoading,
  } = useCascadingVariations(product);

  const { handleAddToCart, isAddingToCart } = useProductAddToCart();
  const [quantity, setQuantity] = useState(1);

  if (isProductLoading || isSelectionLoading) {
    return (
      <div className="mtc-container mtc-product-single-loading">
        <Skeleton height={500} />
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="mtc-container mtc-product-single-error">
        Ürün bulunamadı.
      </div>
    );
  }

  const displayData = selectedVariation || product;
  const isOutOfStock = displayData.stockStatus === "outofstock";
  const isPurchasable = displayData.purchasable;
  const isSelectionComplete = !product.isVariable || !!selectedVariation;
  const isAddToCartDisabled =
    isAddingToCart || !isPurchasable || isOutOfStock || !isSelectionComplete;

  const onAddToCartClick = (e: any) => {
    const idToAdd = selectedVariation ? selectedVariation.id : product.id;
    const variationData = Object.entries(selectedAttributes).map(
      ([attribute, value]) => ({ attribute, value }),
    );
    handleAddToCart(e, {
      productId: idToAdd,
      quantity,
      variation: variationData,
    });
  };

  return (
    <div className="mtc-product-single mtc-container">
      <div className="mtc-product-grid">
        {/* SOL: Görsel Galerisi */}
        <MtcProductGallery
          product={product}
          selectedVariation={selectedVariation}
        />

        {/* SAĞ: Bilgiler ve Custom Varyasyon Dropdownları */}
        <div className="mtc-product-info">
          {/* Ürün Fiyatı */}
          <ProductPriceDisplay
            product={product}
            selectedVariation={selectedVariation}
            className="mtc-product-price"
          />

          {/* Ürün Açıklaması */}
          <div
            className="mtc-product-description"
            dangerouslySetInnerHTML={{
              __html: displayData.shortDescription || product.shortDescription,
            }}
          />

          {/* Varyasyon Dropdownları */}
          <MtcCascadingAttributes
            product={product}
            selectedAttributes={selectedAttributes}
            availableTermsPerAttribute={availableTermsPerAttribute}
            onAttributeSelect={handleAttributeSelect}
            onClear={handleClear}
          />

          {/* Aksiyon Butonları */}
          <div className="mtc-product-actions">
            <QuantityControl
              value={quantity}
              onChange={setQuantity}
              min={1}
              max={displayData.maxStock ?? 99}
              disabled={isAddToCartDisabled}
              ariaLabel="Miktar"
            />
            <button
              className="button button--primary mtc-add-to-cart-btn"
              onClick={onAddToCartClick}
              disabled={isAddToCartDisabled}
            >
              {isAddingToCart ? "Ekleniyor..." : "Sepete Ekle"}
            </button>
          </div>

          <div className="mtc-delivery-estimate-wrapper">
            <DeliveryEstimateDisplay estimate={product.deliveryEstimation} />
          </div>
        </div>
      </div>

      {/* SEKMELER (Plugin'den Alınan Tabs Bileşeni) */}
      {ProductSingleTabs && (
        <ProductSingleTabs
          product={product}
          isProductLoading={isProductLoading}
          isProductError={isError}
        />
      )}
    </div>
  );
};

export default MtcProductSingle;
